import api from "../api";

// Protocolo e distribuição (F85). O certificado e as chaves dos tribunais só sobem; nunca voltam da API.

// F168: com customerId, a equipe age em nome do cliente (tela de detalhes); sem ele, é o próprio cliente.
const scopeOf = (customerId) => (customerId ? `filing/customers/${customerId}` : 'filing');

const findSettings = ({ customerId } = {}) =>
    api.get({ type: 'user', service: `${scopeOf(customerId)}/settings` }).then(({ data }) => data);

// Termo v2: scopes 'lawyer' (o usuário, em nome próprio, com a OAB) e 'account' (só o titular: preço e débito).
const acceptTerm = ({ scopes, oab } = {}) =>
    api.post({ type: 'user', service: 'filing/term', data: { scopes, oab: oab || undefined } });

// Revoga a autorização do próprio usuário: apaga o certificado e as chaves; novo uso exige novo aceite.
const revokeAuthorization = () =>
    api.post({ type: 'user', service: 'filing/term/revoke' }).then(({ data }) => data);

const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const uploadCertificate = async ({ customerUserId, file, password, customerId }) => {
    const pfxBase64 = await readFileAsBase64(file);

    return api
        .post({ type: 'user', service: `${scopeOf(customerId)}/certificates`, data: { customerUserId: customerUserId || null, pfxBase64, password } })
        .then(({ data }) => data);
};

const removeCertificate = (certificateId, { customerId } = {}) =>
    api.remove({ type: 'user', service: `${scopeOf(customerId)}/certificates/${certificateId}` });

// Acesso = grupo de tribunais que dividem o mesmo 2FA (ex.: PJe da Justiça do Trabalho = 1 chave para os 24 TRTs).
// F389: password só no acesso por CPF + senha (Projudi TJPR).
const connectAccess = ({ certificateId, accessKey, secret, code, password, customerId }) =>
    api
        .post({ type: 'user', service: `${scopeOf(customerId)}/certificates/${certificateId}/access/${accessKey}`, data: { secret, code, ...(password ? { password } : {}) } })
        .then(({ data }) => data);

const disconnectAccess = ({ certificateId, accessKey, customerId }) =>
    api.remove({ type: 'user', service: `${scopeOf(customerId)}/certificates/${certificateId}/access/${accessKey}` });

// Códigos dos tribunais (F107). A Facilita ativa o código pelo robô; alreadyUsed = o cliente já desativou o antigo.
const activateAccess = ({ certificateId, accessKey, alreadyUsed, customerId }) =>
    api
        .post({ type: 'user', service: `${scopeOf(customerId)}/certificates/${certificateId}/access/${accessKey}/activate`, data: { alreadyUsed: alreadyUsed === true } })
        .then(({ data }) => data);

// Cada leitura grava uma linha na trilha de acessos: buscar de novo só quando o código vence.
const findCodes = () =>
    api.get({ type: 'user', service: 'filing/codes' }).then(({ data }) => data);

const findCustomerCodes = (customerId) =>
    api.get({ type: 'user', service: `filing/customers/${customerId}/codes` }).then(({ data }) => data);

// Equipe: a lista vem sem código; o código de um acesso sai só no clique (cada busca vai para a trilha).
const findCustomerCode = ({ customerId, certificateId, accessKey }) =>
    api
        .get({ type: 'user', service: `filing/customers/${customerId}/certificates/${certificateId}/access/${accessKey}/code` })
        .then(({ data }) => data);

// Chave do código (QR Code). Só sob clique; fica no estado do componente que a mostrou, nunca em log ou store.
const revealSecret = ({ certificateId, accessKey, customerId }) =>
    api
        .get({
            type: 'user',
            service: customerId
                ? `filing/customers/${customerId}/certificates/${certificateId}/access/${accessKey}/secret`
                : `filing/certificates/${certificateId}/access/${accessKey}/secret`,
        })
        .then(({ data }) => data);

// Resumo do protocolo para os cards da lista (estado, tribunal, nº e comprovante), até 50 solicitações por vez.
const findRequestsFilingSummary = (requestIds) =>
    api
        .get({ type: 'user', service: 'filing/requests-summary', queryString: { ids: requestIds.join(',') } })
        .then(({ data }) => data?.items || {});

// Aba Protocolos da tela de solicitações (F147): cada protocolo e distribuição do cliente, com a etapa e o comprovante.
const findCustomerProtocols = ({ search = '', page = 1, perPage = 20 } = {}) =>
    api
        .get({ type: 'user', service: 'filing/customer-protocols', queryString: { search: search || undefined, page, perPage } })
        .then(({ data }) => data || { items: [], total: 0, active: 0, needsCustomer: 0, page, perPage });

const findCustomerProtocolsSummary = () =>
    api.get({ type: 'user', service: 'filing/customer-protocols/summary' }).then(({ data }) => data || { total: 0, active: 0, needsCustomer: 0 });

const findRequestFiling = (requestId) =>
    api.get({ type: 'user', service: `filing/requests/${requestId}`, queryString: { requirementsVersion: 2 } }).then(({ data }) => data);

// certificateIds: principal primeiro; certificateId segue para a API antiga. documents = lista final dos anexos.
// Meu INSS (F186): segurado = { cpf, govbrPassword } e inssService no lugar do certificado.
// confirmBlankFields (F261): a cliente confirma que a peça está completa depois da recusa PECA_CAMPOS_EM_BRANCO.
// waivedRequirements e listAcknowledged (F268): exigidos dispensados e a confirmação da lista; sem eles a API recusa com
// LISTA_NAO_CONFIRMADA mesmo com a caixa marcada (F347).
const requestFiling = (requestId, { certificateId, certificateIds, courtKey, finalDocumentId, feeSlipNumber, alternatives, requirementDocuments, documents, authorizedMissing, waivedRequirements, listAcknowledged, confirmDeadline, confirmBlankFields, segurado, inssService }) =>
    api
        .post({
            type: 'user',
            service: `filing/requests/${requestId}`,
            data: {
                certificateId: certificateId ?? certificateIds?.[0], certificateIds, courtKey, finalDocumentId, feeSlipNumber, alternatives, requirementDocuments, documents, authorizedMissing, waivedRequirements, listAcknowledged, confirmDeadline, confirmBlankFields, segurado, inssService,
            },
        })
        .then(({ data }) => data);

const cancelFiling = (requestId) =>
    api.post({ type: 'user', service: `filing/requests/${requestId}/cancel` }).then(({ data }) => data);

// Quem do escritório usou o certificado: data, tribunal e peça.
const findAccessLog = ({ page = 1, pageSize = 10, customerId } = {}) =>
    api.get({ type: 'user', service: `${scopeOf(customerId)}/access-log`, queryString: { page, pageSize } }).then(({ data }) => {
        const results = Array.isArray(data) ? data : data?.results || data?.items || [];
        return { results, total: Number(data?.total) || results.length, page: Number(data?.page) || page };
    });

// A API devolve { message, errorCode } (o `code` numérico é o status HTTP).
const errorCodeOf = (error) => {
    const body = error?.response?.data || {};
    if (body.errorCode)
        return body.errorCode;

    return typeof body.code === 'string' ? body.code : null;
};

const errorMessageOf = (error, fallback = 'Não deu certo agora. Tente de novo em instantes.') =>
    error?.response?.data?.message || error?.data?.message || fallback;

const filingService = {
    findSettings,
    acceptTerm,
    revokeAuthorization,
    uploadCertificate,
    removeCertificate,
    connectAccess,
    disconnectAccess,
    activateAccess,
    findCodes,
    findCustomerCodes,
    findCustomerCode,
    revealSecret,
    findRequestsFilingSummary,
    findCustomerProtocols,
    findCustomerProtocolsSummary,
    findRequestFiling,
    requestFiling,
    cancelFiling,
    findAccessLog,
    errorCodeOf,
    errorMessageOf,
};

export default filingService;
